import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { supabase } from '../lib/supabase';
import { Title } from '../components/ui/Title';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Grid } from '../components/ui/Grid';
import { Modal } from '../components/ui/Modal';
import { ConfirmationDialog } from '../components/ui/ConfirmationDialog';
import { Plus, Video, Music, FileText, Image, Download, ExternalLink, Edit, Trash2 } from 'lucide-react';
import { ActionMenu } from '../components/ui/ActionMenu';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MediaUpload, type MediaData } from '../components/MediaUpload';
import type { Band, ExclusiveContent } from '../types/database';

const contentSchema = z.object({
  title: z.string().min(1, 'Title is required').max(100, 'Title too long'),
  description: z.string().max(500, 'Description too long').optional(),
  media: z.object({
    type: z.enum(['image', 'video', 'audio', 'external']),
    url: z.string().url(),
    method: z.enum(['upload', 'url']),
  }),
});

type ContentFormData = z.infer<typeof contentSchema>;

function ManageFanContent() {
  const { t } = useTranslation();
  const { bandSlug } = useParams<{ bandSlug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const [band, setBand] = useState<Band | null>(null);
  const [content, setContent] = useState<ExclusiveContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showContentModal, setShowContentModal] = useState(false);
  const [editingContent, setEditingContent] = useState<ExclusiveContent | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [media, setMedia] = useState<MediaData | null>(null);

  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm<ContentFormData>({
    resolver: zodResolver(contentSchema),
  });

  useEffect(() => {
    async function loadBandAndContent() {
      if (!bandSlug) {
        setError('Band not found');
        setLoading(false);
        return;
      }

      if (!user) {
        setError('You must be logged in to access this page');
        setLoading(false);
        return;
      }

      // Wait for profile to load before checking
      if (profileLoading) {
        return; // Still loading, don't set error yet
      }

      if (!profile) {
        setError('Please complete your profile to access this page');
        setLoading(false);
        return;
      }

      try {
        // Get band details and check if user is admin
        const { data: bandData, error: bandError } = await supabase
          .from('bands')
          .select(`
            *,
            members:band_members(
              profile:profiles(*),
              role
            )
          `)
          .eq('slug', bandSlug)
          .single();

        if (bandError) throw bandError;
        if (!bandData) throw new Error('Band not found');

        // Check if user is admin
        const userMember = bandData.members.find(m => m.profile.id === profile.id);
        if (!userMember || userMember.role !== 'admin') {
          if (!userMember) {
            setError('You must be a member of this band to manage fan content');
          } else {
            setError('You must be a band admin to manage fan content');
          }
          setLoading(false);
          return;
        }

        // Check if fan area is enabled
        if (!bandData.fan_area_enabled) {
          setError('Fan area is not enabled for this band');
          setLoading(false);
          return;
        }

        setBand(bandData);

        // Load exclusive content
        const { data: contentData, error: contentError } = await supabase
          .from('exclusive_content')
          .select('*')
          .eq('band_id', bandData.id)
          .order('created_at', { ascending: false });

        if (contentError) throw contentError;
        setContent(contentData || []);
      } catch (err) {
        console.error('Error loading band and content:', err);
        setError(err instanceof Error ? err.message : 'Failed to load content');
      } finally {
        setLoading(false);
      }
    }

    loadBandAndContent();
  }, [bandSlug, user, profile, profileLoading]);

  const getContentIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <Video className="w-6 h-6" />;
      case 'audio':
        return <Music className="w-6 h-6" />;
      case 'image':
        return <Image className="w-6 h-6" />;
      case 'file':
        return <Download className="w-6 h-6" />;
      case 'link':
        return <ExternalLink className="w-6 h-6" />;
      default:
        return <FileText className="w-6 h-6" />;
    }
  };

  const handleAddContent = () => {
    setEditingContent(null);
    setMedia(null);
    reset({
      title: '',
      description: '',
    });
    setShowContentModal(true);
  };

  const handleEditContent = (contentItem: ExclusiveContent) => {
    setEditingContent(contentItem);
    
    // Map existing content type to media type
    const mediaType = contentItem.type === 'link' ? 'external' : 
                     contentItem.type === 'text' || contentItem.type === 'file' ? 'external' :
                     contentItem.type as 'image' | 'video' | 'audio';
    
    setMedia({
      type: mediaType,
      url: contentItem.content_url,
      method: 'url', // Assume existing content is URLs
    });
    
    reset({
      title: contentItem.title,
      description: contentItem.description || '',
    });
    setShowContentModal(true);
  };

  const handleDeleteContent = async (contentId: string) => {
    try {
      const { error } = await supabase
        .from('exclusive_content')
        .delete()
        .eq('id', contentId);

      if (error) throw error;

      setContent(prev => prev.filter(c => c.id !== contentId));
      setShowDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting content:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete content');
    }
  };

  const onSubmit = async (data: ContentFormData) => {
    if (isSubmitting || !band || !media) return;

    try {
      setIsSubmitting(true);
      setError('');

      const contentData = {
        title: data.title,
        description: data.description,
        content_url: media.url,
        type: media.type === 'external' ? 'link' : media.type,
      };
      if (editingContent) {
        // Update existing content
        const { data: updatedContent, error } = await supabase
          .from('exclusive_content')
          .update({
            ...contentData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingContent.id)
          .select()
          .single();

        if (error) throw error;

        setContent(prev => prev.map(c => c.id === editingContent.id ? updatedContent : c));
      } else {
        // Create new content
        const { data: newContent, error } = await supabase
          .from('exclusive_content')
          .insert({
            ...contentData,
            band_id: band.id,
          })
          .select()
          .single();

        if (error) throw error;

        setContent(prev => [newContent, ...prev]);
      }

      setShowContentModal(false);
      setEditingContent(null);
      setMedia(null);
    } catch (err) {
      console.error('Error saving content:', err);
      setError(err instanceof Error ? err.message : 'Failed to save content');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || profileLoading) {
    return (
      <div className="container mx-auto px-6 py-24">
        <div className="text-center text-light/60">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-6 py-24">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => navigate(`/bands/${bandSlug}`)}
            className="btn-primary"
          >
            Back to Band Page
          </button>
        </div>
      </div>
    );
  }

  if (!band) {
    return (
      <div className="container mx-auto px-6 py-24">
        <div className="text-center text-red-500">Band not found</div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: t('nav.bands'), href: '/bands' },
    { label: band.name, href: `/bands/${bandSlug}` },
    { label: 'Manage Fan Content' },
  ];

  return (
    <div className="container mx-auto px-6 pb-8 md:py-24">
      <div className="mb-6">
        <Breadcrumbs items={breadcrumbItems} />
      </div>

      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-light">{band.name} {t('fanArea.fanContent')}</h1>
          <button
            onClick={handleAddContent}
            className="btn-outline flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Add Content</span>
          </button>
        </div>

        {error && (
          <div className="bg-red-500/10 text-red-500 p-4 rounded-lg mb-6 text-center">
            {error}
          </div>
        )}

        {content.length > 0 ? (
          <Grid cols={3}>
            {content.map((item) => (
              <div key={item.id} className="panel relative group">
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ActionMenu
                    items={[
                      {
                        label: 'Edit Content',
                        icon: <Edit className="w-4 h-4" />,
                        onClick: () => handleEditContent(item),
                      },
                      {
                        label: 'Delete Content',
                        icon: <Trash2 className="w-4 h-4" />,
                        onClick: () => setShowDeleteConfirm(item.id),
                        className: 'text-red-500 hover:text-red-500',
                      },
                    ]}
                  />
                </div>

                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center text-primary">
                    {getContentIcon(item.type)}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-light">{item.title}</h3>
                    <p className="text-sm text-light/60 capitalize">{item.type}</p>
                  </div>
                </div>

                {item.description && (
                  <p className="text-light/80 text-sm mb-4 line-clamp-3">
                    {item.description}
                  </p>
                )}

                <div className="flex items-center justify-between text-sm text-light/60">
                  <span>{new Date(item.created_at).toLocaleDateString()}</span>
                  <a
                    href={item.content_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </Grid>
        ) : (
          <div className="text-center py-12 panel">
            <FileText className="w-16 h-16 text-light/40 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-light mb-2">No Exclusive Content Yet</h3>
            <p className="text-light/60 mb-6">
              Start creating exclusive content for your fans
            </p>
            <button
              onClick={handleAddContent}
              className="underline"
            >
              Add Your First Content
            </button>
          </div>
        )}

        {/* Content Modal */}
        <Modal
          isOpen={showContentModal}
          onClose={() => setShowContentModal(false)}
          title={editingContent ? 'Edit Content' : 'Add New Content'}
        >
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-light/80 mb-2">
                Title
              </label>
              <input
                type="text"
                id="title"
                {...register('title')}
                className="w-full px-4 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:border-gray-custom/40 focus:outline-none"
                placeholder="Enter content title..."
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-500">{errors.title.message}</p>
              )}
            </div>


            <div>
              <label className="block text-sm font-medium text-light/80 mb-4">
                Media Content
              </label>
              <MediaUpload
                value={media}
                onChange={setMedia}
                storageContainer="exclusive-content-media"
                error={!media ? 'Please add media content' : undefined}
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-light/80 mb-2">
                Description (Optional)
              </label>
              <textarea
                id="description"
                rows={4}
                {...register('description')}
                className="w-full px-4 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:border-gray-custom/40 focus:outline-none"
                placeholder="Describe this exclusive content..."
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
              )}
            </div>

            <div className="flex flex-col md:flex-row justify-end md:space-x-2 pt-4">
              <button
                type="button"
                onClick={() => setShowContentModal(false)}
                className="btn-outline order-2 mt-6 md:mt-0"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary md:order-2"
                disabled={isSubmitting || !media}
              >
                {isSubmitting ? 'Saving...' : editingContent ? 'Update Content' : 'Add Content'}
              </button>
            </div>
          </form>
        </Modal>

        {/* Delete Confirmation */}
        <ConfirmationDialog
          isOpen={!!showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(null)}
          onConfirm={() => showDeleteConfirm && handleDeleteContent(showDeleteConfirm)}
          title="Delete Content"
          message="Are you sure you want to delete this exclusive content? This action cannot be undone."
          confirmLabel="Delete Content"
          isDanger={true}
        />
      </div>
    </div>
  );
}

export default ManageFanContent;